import { MusicTabIcon, ShopIcon } from "@assets/svg";

import { ERoutes } from "@utils/constants";

import { DropdownNavItem, MegaMenuGroup } from "./types";

// ========================================
// MEGA MENU CONFIG (desktop)
// SVG icon paths use viewBox="0 0 24 24", stroke-based
// ========================================

export const megaMenuGroups: MegaMenuGroup[] = [
  {
    key: "licensing",
    translationKey: "licensing",
    // Order set 2026-07-26 (user): free use first, then licensing, then the
    // creator programme. The Test tile stays last as the highlighted CTA.
    items: [
      {
        route: ERoutes.FreeUseTerms,
        title: "YouTube Use",
        description: "Free & Premium Background Music",
        // The YouTube mark: the rounded screen plus the play triangle.
        iconPath: "M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z M9.75 15.02l5.75-3.27-5.75-3.27v6.54z",
      },
      {
        route: ERoutes.CatalogLandingLicensing,
        title: "Licensing",
        description: "Projects outside of YouTube",
        // The document icon that used to sit on Free YouTube Use.
        iconPath: "M8 21h12a2 2 0 0 0 2-2v-2H10v2a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v3h4 M19 17V5a2 2 0 0 0-2-2H4",
      },
      {
        route: ERoutes.MUSIC_CREATOR_PROGRAM,
        title: "Music & Ambient Creators",
        description: "Licensing for music channel creators",
        // A music note.
        iconPath: "M9 18V5l12-2v13 M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M18 19a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
      },
      {
        route: ERoutes.Test,
        title: "Free, Subscription or License?",
        description: "Get an answer in 30 seconds",
        iconPath: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z M9 12l2 2 4-4",
        badge: "Test",
        isHighlighted: true,
      },
    ],
  },
  {
    key: "contact",
    translationKey: "contacts",
    items: [
      {
        route: ERoutes.Contact,
        title: "Contact Form",
        description: "We'd love to hear from you",
        iconPath: "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z M22 6l-10 7L2 6",
      },
      {
        route: ERoutes.Licensing,
        title: "License Request",
        description: "Custom solutions for your endeavors",
        iconPath: "M12 20h9 M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z",
      },
      {
        route: ERoutes.Submit,
        title: "Submit Music",
        description: "We are looking for talented musicians",
        iconPath: "M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z M19 10v2a7 7 0 0 1-14 0v-2 M12 19v4 M8 23h8",
      },
    ],
  },
];

export const freeUseNavItems: DropdownNavItem[] = [
  {
    route: ERoutes.CatalogLandingFreeBackground,
    key: "freeBgMusic",
    icon: <MusicTabIcon width={20} height={20} />,
  },
  { route: ERoutes.FreeUseTerms, key: "freeUseSubitem" },
  { route: ERoutes.Test, key: "freeUseOrLicense" },
];

export const licensingNavItems: DropdownNavItem[] = [
  { route: ERoutes.CatalogLandingLicensing, key: "generalLicensing" },
  { route: ERoutes.MUSIC_CREATOR_PROGRAM, key: "ytCreatorProgram" },
];

export const contactNavItems: DropdownNavItem[] = [
  { route: ERoutes.Contact, key: "contactFormular" },
  { route: ERoutes.Licensing, key: "licenseReq" },
  { route: ERoutes.Submit, key: "submitMusic" },
];

export const dropdownNavItems = [
  { items: freeUseNavItems, key: "Free Use" },
  { items: licensingNavItems, key: "licensing" },
  { items: contactNavItems, key: "contacts" },
];

export const userMenuNavItems = [
  { route: ERoutes.Deals, key: "bestDeals" },
  { route: ERoutes.Newsletter, key: "newsletter" },
  { route: ERoutes.DownloadInstruction, key: "downloadInstructions" },
  { route: ERoutes.MUSIC_CREATOR_PROGRAM, key: "ytCreatorProgram" },
];

// ========================================
// SUBTAB ITEMS — sibling pages only
// ========================================

// Catalog page — just Free Use Terms (Free Music removed since you're already browsing)
const _catalogItems = [
  { route: ERoutes.FreeUseTerms, key: "Free Use Terms" },
];
const catalogItemsMobile = [
  { route: ERoutes.FreeUseTerms, key: "Terms" },
];

// Free music section siblings
const freeMusicItems = [
  { route: ERoutes.CatalogLandingFreeBackground, key: "Free Music" },
  { route: ERoutes.FreeUseTerms, key: "Free Use Terms" },
];
const freeMusicItemsMobile = [
  { route: ERoutes.CatalogLandingFreeBackground, key: "Free Music" },
  { route: ERoutes.FreeUseTerms, key: "Terms" },
];

// Licensing section siblings
const licensingItems = [
  { route: ERoutes.CatalogLandingLicensing, key: "Licensing Info" },
  { route: ERoutes.Licensing, key: "License Request" },
  { route: ERoutes.MUSIC_CREATOR_PROGRAM, key: "Creator Program" },
];
const licensingItemsMobile = [
  { route: ERoutes.CatalogLandingLicensing, key: "Licensing" },
  { route: ERoutes.Licensing, key: "Request" },
  { route: ERoutes.MUSIC_CREATOR_PROGRAM, key: "Creator" },
];

// Contact section siblings
const contactItems = [
  { route: ERoutes.Contact, key: "Contact" },
  { route: ERoutes.Submit, key: "Submit Music" },
];
const contactItemsMobile = [
  { route: ERoutes.Contact, key: "Contact" },
  { route: ERoutes.Submit, key: "Submit" },
];

// Deals
const dealsItems = [
  { route: ERoutes.Deals, key: "Deals" },
  { route: ERoutes.Shop, key: "Shop", icon: <ShopIcon />, target: "_blank" as const },
];

// Legal
const legalItems = [
  { route: ERoutes.Conditions, key: "Terms & Conditions" },
  { route: ERoutes.Privacy, key: "Privacy Policy" },
  { route: "" as ERoutes, key: "Cookies" },
];
const legalItemsMobile = [
  { route: ERoutes.Conditions, key: "Terms" },
  { route: ERoutes.Privacy, key: "Privacy" },
  { route: "" as ERoutes, key: "Cookies" },
];

export const navTabsDesktop = {
  [ERoutes.Home]: [],
  [ERoutes.Catalog]: [],
  [ERoutes.TrackForMapping]: [],
  [ERoutes.AlbumForMapping]: [],
  [ERoutes.CatalogLandingFreeBackground]: freeMusicItems,
  [ERoutes.FreeUseTerms]: freeMusicItems,
  [ERoutes.CatalogLandingLicensing]: licensingItems,
  [ERoutes.Licensing]: licensingItems,
  [ERoutes.MUSIC_CREATOR_PROGRAM]: licensingItems,
  [ERoutes.Contact]: contactItems,
  [ERoutes.Submit]: contactItems,
  [ERoutes.Deals]: dealsItems,
  [ERoutes.DownloadInstruction]: [],
  [ERoutes.Newsletter]: [],
  [ERoutes.Favorites]: [],
  [ERoutes.Profile]: [],
  [ERoutes.Conditions]: legalItems,
  [ERoutes.Privacy]: legalItems,
};

export const navTabsMobile = {
  [ERoutes.Home]: [],
  [ERoutes.Catalog]: catalogItemsMobile,
  [ERoutes.TrackForMapping]: [],
  [ERoutes.AlbumForMapping]: [],
  [ERoutes.CatalogLandingFreeBackground]: freeMusicItemsMobile,
  [ERoutes.FreeUseTerms]: freeMusicItemsMobile,
  [ERoutes.CatalogLandingLicensing]: licensingItemsMobile,
  [ERoutes.Licensing]: licensingItemsMobile,
  [ERoutes.MUSIC_CREATOR_PROGRAM]: licensingItemsMobile,
  [ERoutes.Contact]: contactItemsMobile,
  [ERoutes.Submit]: contactItemsMobile,
  [ERoutes.Deals]: dealsItems,
  [ERoutes.DownloadInstruction]: [],
  [ERoutes.Newsletter]: [],
  [ERoutes.Favorites]: [],
  [ERoutes.Profile]: [],
  [ERoutes.Conditions]: legalItemsMobile,
  [ERoutes.Privacy]: legalItemsMobile,
};
